import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Vehicle } from '../models/vehicle.model';

/**
 * Service to manage the currently selected vehicle context
 * Similar to TeamContextService but for vehicle selection
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleContextService {
  private readonly STORAGE_KEY = 'selected_vehicle';
  private selectedVehicleSubject = new BehaviorSubject<Vehicle | null>(this.loadFromStorage());

  /**
   * Observable stream of the currently selected vehicle
   */
  public selectedVehicle$: Observable<Vehicle | null> = this.selectedVehicleSubject.asObservable();

  constructor() {}

  /**
   * Get the currently selected vehicle
   */
  getSelectedVehicle(): Vehicle | null {
    return this.selectedVehicleSubject.value;
  }

  /**
   * Set the currently selected vehicle and persist to storage
   */
  setSelectedVehicle(vehicle: Vehicle | null): void {
    this.selectedVehicleSubject.next(vehicle);
    if (vehicle) {
      this.saveToStorage(vehicle);
    } else {
      this.clearStorage();
    }
  }

  /**
   * Clear the currently selected vehicle
   */
  clearSelectedVehicle(): void {
    this.selectedVehicleSubject.next(null);
    this.clearStorage();
  }

  /**
   * Check if a vehicle is currently selected
   */
  hasSelectedVehicle(): boolean {
    return this.selectedVehicleSubject.value !== null;
  }

  /**
   * Get the selected vehicle ID (convenience method)
   */
  getSelectedVehicleId(): string | null {
    const vehicle = this.selectedVehicleSubject.value;
    return vehicle ? vehicle.id : null;
  }

  /**
   * Require a selected vehicle (throws error if none selected)
   */
  requireSelectedVehicle(): Vehicle {
    const vehicle = this.selectedVehicleSubject.value;
    if (!vehicle) {
      throw new Error('No vehicle selected. Please select a vehicle to continue.');
    }
    return vehicle;
  }

  /**
   * Check if the given vehicle is currently selected
   */
  isVehicleSelected(vehicleId: string): boolean {
    const selectedVehicle = this.selectedVehicleSubject.value;
    return selectedVehicle !== null && selectedVehicle.id === vehicleId;
  }

  /**
   * Load vehicle from localStorage
   */
  private loadFromStorage(): Vehicle | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as Vehicle;
      }
    } catch (error) {
      console.error('Error loading vehicle from storage:', error);
      this.clearStorage();
    }
    return null;
  }

  /**
   * Save vehicle to localStorage
   */
  private saveToStorage(vehicle: Vehicle): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(vehicle));
    } catch (error) {
      console.error('Error saving vehicle to storage:', error);
    }
  }

  /**
   * Clear vehicle from localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing vehicle from storage:', error);
    }
  }
}
